import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthProvider";
import {
  type Agendamento,
  AgendamentoService,
  type Pet,
  type TipoAgendamento,
} from "@/services/app/agendamento";
import { PetsService } from "@/services/app/pets";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Bell,
  Calendar,
  Cat,
  CheckCircle,
  ChevronRight,
  Clock,
  Dog,
  FileText,
  Heart,
  History,
  Info,
  Loader2,
  Plus,
  Trash2,
  X
} from "lucide-react";
import moment from "moment";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

const agendamentoSchema = z.object({
  tipoId: z.string().min(1, "Tipo de serviço é obrigatório"),
  petId: z.string().min(1, "Selecione um pet"),
  observacao: z.string().min(1, "Observação é obrigatória"),
});

const cancelamentoSchema = z.object({
  motivo: z.string().min(1, "Motivo do cancelamento é obrigatório"),
});

type AgendamentoFormData = z.infer<typeof agendamentoSchema>;
type CancelamentoFormData = z.infer<typeof cancelamentoSchema>;

type ModalType = "formulario" | "detalhes" | "semProcedimentos" | null;

interface TipoOption {
  id: string;
  name: string;
  hasDisponibilidade: boolean;
}

export default function Agendamentos() {
  const [agendamentoSelecionado, setAgendamentoSelecionado] =
    useState<Agendamento | null>(null);
  const [modalAberto, setModalAberto] = useState<ModalType>(null);
  const [petSelecionado, setPetSelecionado] = useState<Pet | null>(null);
  const [agendamentoConfirmado, setAgendamentoConfirmado] = useState<{
    nomePet: string;
    data: string;
    turno: string;
  } | null>(null);
  const [agendamentoRecemCriado, setAgendamentoRecemCriado] = useState<Agendamento | null>(null);
  const [modalConfirmacaoAberto, setModalConfirmacaoAberto] = useState(false);
  const [modalIndisponivelAberto, setModalIndisponivelAberto] = useState(false);
  const [modalCancelamentoAberto, setModalCancelamentoAberto] = useState(false);
  const [tipoIndisponivel, setTipoIndisponivel] = useState<string | null>(null);

  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const petService = new PetsService();
  const agendamentoService = new AgendamentoService();

  // Queries
  const { data: pets, isLoading: isLoadingPets } = useQuery({
    queryKey: ["petsAgendamento", user?.id],
    queryFn: async () => {
      return (await petService.getPets()).filter((e) => e.obito === null);
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2, // 2 minutos
    gcTime: 1000 * 60 * 5, // 5 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const {
    data: agendamentosData,
    isLoading: isLoadingAgendamentos,
    refetch,
  } = useQuery({
    queryKey: ["agendamentos", user?.id],
    queryFn: async () => {
      const agendamentos = await agendamentoService.getAgendamentos();
      return agendamentos.sort((a, b) => {
        type StatusType = "AGENDADA" | "PENDENTE" | "CANCELADA" | "REALIZADA";
        const statusPriority: Record<StatusType, number> = {
          AGENDADA: 0,
          PENDENTE: 1,
          CANCELADA: 3,
          REALIZADA: 2,
        };

        if (
          statusPriority[a.status as StatusType] !==
          statusPriority[b.status as StatusType]
        ) {
          return (
            statusPriority[a.status as StatusType] -
            statusPriority[b.status as StatusType]
          );
        }

        if (a.data && b.data) {
          return new Date(a.data).getTime() - new Date(b.data).getTime();
        }
        return 0;
      });
    },
  });

  // Verificar se há parâmetro de detalhes na URL
  const detalhesId = searchParams.get("detalhes");

  // Efeito para abrir modal de detalhes automaticamente
  useEffect(() => {
    if (detalhesId && agendamentosData && !modalAberto) {
      const agendamento = agendamentosData.find(
        (item) => item.id === detalhesId
      );
      if (agendamento) {
        setAgendamentoSelecionado(agendamento);
        setModalAberto("detalhes");
        // Limpar o parâmetro da URL
        setSearchParams({});
      }
    }
  }, [detalhesId, agendamentosData, modalAberto, setSearchParams]);

  const { data: tipos } = useQuery<TipoOption[]>({
    queryKey: ["tipos"],
    queryFn: async () => {
      const response = await agendamentoService.getMotivos();

      return response.map((tipo: TipoAgendamento) => ({
        id: tipo.id,
        name: tipo.nome,
        hasDisponibilidade: tipo.hasDisponibilidade,
      }));
    },
    staleTime: 30 * 1000, // 30 segundos para desenvolvimento
    gcTime: 2 * 60 * 1000, // 2 minutos
    refetchOnWindowFocus: true, // Refazer busca quando a janela ganhar foco
  });

  // Mutations
  const createAgendamentoMutation = useMutation({
    mutationFn: (data: AgendamentoFormData) => {
      return agendamentoService.createAgendamento({
        tipoId: data.tipoId,
        petId: data.petId,
        observacao: data.observacao || "",
      });
    },
    onSuccess: (response) => {
      // Buscar o nome do pet selecionado
      const petNome =
        pets?.find((pet) => pet.id === petSelecionado?.id)?.nome || "seu pet";

      // Armazenar o agendamento recém-criado completo
      setAgendamentoRecemCriado(response);

      // Configurar dados do agendamento confirmado
      setAgendamentoConfirmado({
        nomePet: petNome,
        data: moment(response?.data).format("DD/MM/YYYY"),
        turno: response?.turno?.toLowerCase(),
      });

      // Fechar modal de formulário
      fecharModal();

      // Abrir modal de confirmação independente
      setModalConfirmacaoAberto(true);

      queryClient.invalidateQueries({ queryKey: ["agendamentos"] });
      refetch();
    },
    onError: (error) => {
      console.error(error);
      toast.error(error.message || "Ocorreu um erro ao criar o agendamento.");
    },
  });

  const cancelarAgendamentoMutation = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) => {
      return agendamentoService.cancelarAgendamento(id, motivo);
    },
    onSuccess: () => {
      toast.success("Agendamento cancelado com sucesso.");
      fecharModalCancelamento();
      fecharModal();
      setAgendamentoRecemCriado(null);
      queryClient.invalidateQueries({ queryKey: ["agendamentos"] });
    },
    onError: (error) => {
      console.error(error);
      toast.error(
        error.message || "Ocorreu um erro ao cancelar o agendamento."
      );
    },
  });

  // Form
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<AgendamentoFormData>({
    resolver: zodResolver(agendamentoSchema),
    defaultValues: {
      tipoId: "",
      petId: "",
      observacao: "",
    },
  });

  // Form de Cancelamento
  const {
    control: controlCancelamento,
    handleSubmit: handleSubmitCancelamento,
    formState: { errors: errorsCancelamento },
    reset: resetCancelamento,
  } = useForm<CancelamentoFormData>({
    resolver: zodResolver(cancelamentoSchema),
    defaultValues: {
      motivo: "",
    },
  });

  // Monitorar o campo tipoId para validação automática
  const watchedTipoId = watch("tipoId");

  // Efeito para validar consistência do formulário quando os tipos mudarem
  useEffect(() => {
    if (tipos && modalAberto === "formulario" && watchedTipoId) {
      const tipoAtual = tipos.find((tipo) => tipo.id === watchedTipoId);

      if (!tipoAtual || tipoAtual.hasDisponibilidade === false) {
        setValue("tipoId", "");
      }
    }
  }, [tipos, modalAberto, watchedTipoId, setValue]);

  // Functions
  const abrirModal = (tipo: ModalType) => {
    setModalAberto(tipo);
  };

  const fecharModal = () => {
    setModalAberto(null);
    setAgendamentoSelecionado(null);
    setPetSelecionado(null);
    reset();
  };

  const fecharModalConfirmacao = () => {
    setModalConfirmacaoAberto(false);
    setAgendamentoConfirmado(null);
    setAgendamentoRecemCriado(null);
  };

  const abrirCancelamentoDoRecemCriado = () => {
    if (agendamentoRecemCriado) {
      setAgendamentoSelecionado(agendamentoRecemCriado);
      setModalConfirmacaoAberto(false);
      abrirModalCancelamento();
    }
  };

  const fecharModalIndisponivel = () => {
    setModalIndisponivelAberto(false);
    setTipoIndisponivel(null);
    setValue("tipoId", "");
  };

  const fecharModalCancelamento = () => {
    setModalCancelamentoAberto(false);
    resetCancelamento();
  };

  const abrirModalCancelamento = () => {
    setModalCancelamentoAberto(true);
    resetCancelamento();
  };

  const abrirNovoAgendamento = () => {
    // Invalidar cache dos tipos para garantir dados atualizados
    queryClient.invalidateQueries({ queryKey: ["tipos"] });

    // Verificar se há tipos de procedimentos disponíveis
    if (!tipos || tipos.length === 0) {
      abrirModal("semProcedimentos");
      return;
    }

    // Limpar todos os estados relacionados ao modal de indisponibilidade
    setModalIndisponivelAberto(false);
    setTipoIndisponivel(null);

    setAgendamentoSelecionado(null);
    setPetSelecionado(null);
    reset();
    abrirModal("formulario");
  };

  const visualizarAgendamento = (agendamento: Agendamento) => {
    setAgendamentoSelecionado(agendamento);
    abrirModal("detalhes");
  };

  const selecionarPet = (pet: Pet) => {
    setPetSelecionado(pet);
    setValue("petId", pet.id || "");
  };

  const onSubmitAgendamento = async (data: AgendamentoFormData) => {
    createAgendamentoMutation.mutate(data);
  };

  const excluirAgendamento = () => {
    if (!agendamentoSelecionado) return;
    abrirModalCancelamento();
  };

  const onSubmitCancelamento = async (data: CancelamentoFormData) => {
    if (!agendamentoSelecionado) return;
    cancelarAgendamentoMutation.mutate({
      id: agendamentoSelecionado.id,
      motivo: data.motivo,
    });
  };

  // Helper functions
  const getEspecieLabel = (especie: string): string => {
    return especie === "CACHORRO" ? "Cão" : "Gato";
  };

  const getTipoServicoLabel = (tipoId: string): string => {
    const tipo = tipos?.find(
      (t: { id: string; name: string }) => t.id === tipoId
    );
    return tipo ? tipo.name : "";
  };

  const getStatusLabel = (status: string): string => {
    return status.charAt(0).toUpperCase() + status.toLowerCase().slice(1);
  };

  // Render functions
  const renderAgendamentoItem = (item: Agendamento) => (
    <div
      key={item.id}
      onClick={() => visualizarAgendamento(item)}
      className="bg-white rounded-xl p-4 mb-4 border border-gray-100 hover:shadow-md transition-all cursor-pointer"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div
            className={`rounded-full p-3 mr-3 ${
              item.pet.especie === "CACHORRO" ? "bg-blue-100" : "bg-red-100"
            }`}
          >
            {item.pet.especie === "CACHORRO" ? (
              <Dog
                className={`h-6 w-6 ${
                  item.pet.especie === "CACHORRO"
                    ? "text-blue-600"
                    : "text-red-600"
                }`}
              />
            ) : (
              <Cat className="h-6 w-6 text-red-600" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">{item.pet.nome}</h3>
            <p className="text-gray-500">{getEspecieLabel(item.pet.especie)}</p>
            <div className="flex items-center mt-1 gap-2">
              <div className="flex items-center">
                <div
                  className={`h-2 w-2 rounded-full mr-2 ${
                    item.status === "PENDENTE"
                      ? "bg-yellow-500"
                      : item.status === "AGENDADA"
                      ? "bg-blue-600"
                      : item.status === "REALIZADA"
                      ? "bg-green-600"
                      : "bg-red-500"
                  }`}
                />
                <span
                  className={`font-medium ${
                    item.status === "PENDENTE"
                      ? "text-yellow-600"
                      : item.status === "AGENDADA"
                      ? "text-blue-600"
                      : item.status === "REALIZADA"
                      ? "text-green-600"
                      : "text-red-500"
                  }`}
                >
                  {getStatusLabel(item.status)}
                </span>
              </div>
              {item.status === "AGENDADA" && item.data && (
                <div className="flex items-center bg-gray-50 px-2 py-1 rounded-full">
                  <Calendar className="h-1 w-1 text-gray-600 mr-1" />
                  <span className="text-gray-600 font-normal text-xs">
                    {moment(item.data).format("DD/MM/YYYY")} • {item.turno}
                  </span>
                </div>
              )}
              {item.status === "REALIZADA" && (
                <div className="flex items-center bg-gray-50 px-2 py-1 rounded-full">
                  <CheckCircle className="h-3 w-3 text-green-600 mr-1" />
                  <span className="text-gray-600 font-normal text-xs">
                    {moment(item.updatedAt).format("DD/MM/YYYY [às] HH:mm")}
                  </span>
                </div>
              )}
              {item.status === "PENDENTE" && (
                <div className="flex items-center bg-gray-50 px-2 py-1 rounded-full">
                  <Clock className="h-3 w-3 text-yellow-600 mr-1" />
                  <span className="text-gray-600 font-normal text-xs">
                    Aguardando confirmação
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400" />
      </div>
    </div>
  );

  const renderPetItem = (
    item: Pet,
    agendamentos: Agendamento[] | undefined
  ) => {
    const hasPendingOrScheduled = agendamentos?.some(
      (agendamento) =>
        agendamento.petId === item.id &&
        (agendamento.status === "PENDENTE" || agendamento.status === "AGENDADA")
    );

    return (
      <div
        key={item.id}
        onClick={() => !hasPendingOrScheduled && selecionarPet(item)}
        className={`${
          petSelecionado?.id === item.id
            ? "border-2 border-primary"
            : "border border-gray-100"
        } bg-white rounded-xl p-4 mb-4 w-full transition-all ${
          hasPendingOrScheduled
            ? "opacity-50 cursor-not-allowed"
            : "cursor-pointer hover:shadow-md select-none"
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div
              className={`rounded-full p-3 mr-3 ${
                item.especie === "CACHORRO" ? "bg-blue-100" : "bg-red-100"
              }`}
            >
              {item.especie === "CACHORRO" ? (
                <Dog
                  className={`h-6 w-6 ${
                    item.especie === "CACHORRO"
                      ? "text-blue-600"
                      : "text-red-600"
                  }`}
                />
              ) : (
                <Cat className="h-6 w-6 text-red-600" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">{item.nome}</h3>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header>
        <div className="max-w-7xl mx-auto px-2">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Agendamentos
                </h1>
                <p className="text-sm text-gray-500">
                  Gerencie seus agendamentos
                </p>
              </div>
            </div>
            <button
              onClick={abrirNovoAgendamento}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Novo
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-2 py-8">
        {isLoadingAgendamentos && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        )}

        {!isLoadingAgendamentos &&
          (!agendamentosData || agendamentosData.length === 0) && (
            <div className="text-center py-12">
              <div className="text-gray-500 mb-4">
                <Calendar className="h-16 w-16 mx-auto opacity-30" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhum agendamento encontrado
              </h3>
              <p className="text-gray-500 mb-6">
                Comece criando seu primeiro agendamento
              </p>
              <button
                onClick={abrirNovoAgendamento}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                Novo Agendamento
              </button>
            </div>
          )}

        {agendamentosData && agendamentosData.length > 0 && (
          <div className="space-y-4">
            {agendamentosData.map((agendamento) =>
              renderAgendamentoItem(agendamento)
            )}
          </div>
        )}
      </main>

      {/* Modal de Formulário */}
      <Sheet open={modalAberto === "formulario"} onOpenChange={fecharModal}>
        <SheetContent
          className="w-full sm:max-w-7xl mx-auto pt-safe max-h-[85vh] overflow-hidden flex flex-col p-0"
          side="bottom"
        >
          <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <SheetHeader>
              <SheetTitle>Novo Agendamento</SheetTitle>
              <SheetDescription>
                Crie um novo agendamento para seu pet
              </SheetDescription>
            </SheetHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
            <form
              onSubmit={handleSubmit(onSubmitAgendamento)}
              className="space-y-6"
            >
              {/* Tipo de Serviço */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <h3 className="text-lg font-bold text-blue-800 mb-2">
                  Tipo de Serviço
                </h3>
                <Controller
                  control={control}
                  name="tipoId"
                  render={({ field: { onChange, value } }) => (
                    <select
                      value={value}
                      onChange={(e) => {
                        const selectedId = e.target.value;

                        // Sempre limpar primeiro para evitar estados inconsistentes
                        if (selectedId === "") {
                          onChange("");
                          return;
                        }

                        const selectedTipo = tipos?.find(
                          (tipo: TipoOption) => tipo.id === selectedId
                        );

                        // Verificar se o tipo existe e é válido
                        if (!selectedTipo) {
                          onChange("");
                          return;
                        }

                        // Verificação rigorosa da disponibilidade
                        if (selectedTipo.hasDisponibilidade === false) {
                          setTipoIndisponivel(selectedTipo.name);
                          setModalIndisponivelAberto(true);
                          return;
                        }

                        // Só definir o valor se o tipo for disponível
                        onChange(selectedId);
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary focus:border-transparent h-12"
                    >
                      <option value="">Selecione um tipo de serviço</option>
                      {tipos?.map((tipo: TipoOption) => (
                        <option key={tipo.id} value={tipo.id}>
                          {tipo.name}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.tipoId && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.tipoId.message}
                  </p>
                )}
              </div>

              {/* Selecionar Pet */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <h3 className="text-lg font-bold text-blue-800 mb-2">
                  Selecione um Pet
                </h3>

                {isLoadingPets ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      {pets?.map((pet) =>
                        renderPetItem(pet as Pet, agendamentosData)
                      )}
                    </div>
                    {errors.petId && (
                      <p className="text-red-500 text-xs mt-2">
                        {errors.petId.message}
                      </p>
                    )}
                    {(!pets || pets.length === 0) && (
                      <div className="text-center py-4">
                        <p className="text-gray-500">Nenhum pet encontrado</p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Observação */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observação
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Controller
                    control={control}
                    name="observacao"
                    render={({ field }) => (
                      <textarea
                        {...field}
                        rows={4}
                        placeholder="Descreva o motivo do agendamento"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    )}
                  />
                </div>
                {errors.observacao && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.observacao.message}
                  </p>
                )}
              </div>

              {/* Botão */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={createAgendamentoMutation.isPending}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {createAgendamentoMutation.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  Criar Agendamento
                </button>
              </div>
            </form>
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal de Detalhes */}
      <Sheet open={modalAberto === "detalhes"} onOpenChange={fecharModal}>
        <SheetContent
          className="w-full sm:max-w-7xl mx-auto pt-safe max-h-[85vh] overflow-hidden flex flex-col p-0"
          side="bottom"
        >
          {agendamentoSelecionado && (
            <>
              <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                <SheetHeader>
                  <SheetTitle>Detalhes do Agendamento</SheetTitle>
                  <SheetDescription>
                    Informações do agendamento selecionado
                  </SheetDescription>
                </SheetHeader>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
                <div className="bg-gray-50 p-4 rounded-xl mb-4">
                  <div className="flex items-center mb-4">
                    <div
                      className={`rounded-full p-3 mr-3 ${
                        agendamentoSelecionado.pet.especie === "CACHORRO"
                          ? "bg-blue-100"
                          : "bg-red-100"
                      }`}
                    >
                      {agendamentoSelecionado.pet.especie === "CACHORRO" ? (
                        <Dog
                          className={`h-6 w-6 ${
                            agendamentoSelecionado.pet.especie === "CACHORRO"
                              ? "text-blue-600"
                              : "text-red-600"
                          }`}
                        />
                      ) : (
                        <Cat className="h-6 w-6 text-red-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">
                        {agendamentoSelecionado.pet.nome}
                      </h3>
                      <p className="text-gray-500">
                        {getEspecieLabel(agendamentoSelecionado.pet.especie)}
                      </p>
                    </div>
                  </div>

                  <div className="h-0.5 bg-gray-200 my-4" />

                  <div className="space-y-4">
                    <div>
                      <p className="text-gray-600 text-sm mb-1">
                        Tipo de Serviço
                      </p>
                      <p className="text-gray-900 font-medium text-lg line-clamp-1">
                        {getTipoServicoLabel(agendamentoSelecionado.tipoId)} {agendamentoSelecionado?.campanhaId && (
                          <span className="text-gray-500 text-sm">
                           - {agendamentoSelecionado?.campanha?.nome}
                          </span>
                        )}
                      </p>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-sm mb-1">Status</p>
                        <p className="text-gray-900 font-medium text-lg">
                          {getStatusLabel(agendamentoSelecionado.status)}
                        </p>
                      </div>

                      {agendamentoSelecionado.status === "AGENDADA" &&
                        agendamentoSelecionado.data && (
                          <div>
                            <p className="text-gray-600 text-sm mb-1">
                              Agendado para
                            </p>
                            <p className="text-gray-900 font-medium text-lg">
                              {moment(agendamentoSelecionado.data).format(
                                "DD/MM/YYYY"
                              )}{" "}
                              {agendamentoSelecionado.turno}
                            </p>
                          </div>
                        )}
                    </div>

                    {agendamentoSelecionado.observacao && (
                      <div>
                        <p className="text-gray-600 text-sm mb-1">Observação</p>
                        <p className="text-gray-900 font-medium text-lg">
                          {agendamentoSelecionado.observacao}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Botões de Ação */}
                <div className="pt-6 space-y-3">
                  {/* Botão Ver Histórico */}
                  <button
                    onClick={() =>
                      navigate(
                        `/historico?detalhes=${agendamentoSelecionado.id}`
                      )
                    }
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <History className="h-4 w-4" />
                    Ver Histórico
                  </button>

                  {/* Botão Cancelar (apenas se não estiver cancelado ou realizado) */}
                  {agendamentoSelecionado.status !== "CANCELADA" &&
                    agendamentoSelecionado.status !== "REALIZADA" && (
                      <button
                        onClick={excluirAgendamento}
                        disabled={
                          cancelarAgendamentoMutation.isPending ||
                          moment(agendamentoSelecionado.data).isBefore(moment())
                        }
                        className="w-full bg-red-500 hover:bg-red-600 text-white py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 disabled:hover:bg-red-500"
                      >
                        {cancelarAgendamentoMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Trash2 className="h-4 w-4" />
                            Cancelar Agendamento
                          </>
                        )}
                      </button>
                    )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Modal de Confirmação Independente */}
      <Sheet
        open={modalConfirmacaoAberto}
        onOpenChange={fecharModalConfirmacao}
      >
        <SheetContent
          className="w-full sm:max-w-7xl mx-auto pt-safe max-h-[85vh] overflow-hidden flex flex-col p-0"
          side="bottom"
        >
          <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
            <SheetHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <SheetTitle className="text-gray-800 text-left">
                    Agendamento Confirmado!
                  </SheetTitle>
                  <SheetDescription className="text-gray-500 text-left">
                    Seu agendamento foi realizado com sucesso
                  </SheetDescription>
                </div>
              </div>
            </SheetHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {agendamentoConfirmado && (
              <div className="space-y-4">
                {/* Informações principais */}
                <div className="bg-white rounded-xl p-4 space-y-3 border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-gray-500">
                        Data do atendimento
                      </p>
                      <p className="font-semibold text-lg text-gray-800">
                        {agendamentoConfirmado.data}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-gray-500">Turno</p>
                      <p className="font-semibold text-lg text-gray-800 uppercase">
                        {agendamentoConfirmado.turno}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Mensagem principal */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="space-y-3 flex-1">
                      <h3 className="font-semibold text-gray-800">
                        Informações Importantes
                      </h3>
                      <div className="text-gray-700 leading-relaxed space-y-3">
                      

                        {/* Seção de Horários */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-blue-800 font-medium mb-2 flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            ATENÇÃO - HORÁRIOS DE CHEGADA:
                          </p>
                          <ul className="text-blue-700 text-sm space-y-1">
                            <li className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                              <strong>Atendimentos da manhã:</strong> Chegar até
                              às <strong>08:00 horas</strong>
                            </li>
                            <li className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                              <strong>Atendimentos da tarde:</strong> Chegar até
                              às <strong>14:00 horas</strong>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Botões de ação */}
          <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0 space-y-3">
            <button
              onClick={fecharModalConfirmacao}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 shadow-sm active:scale-95"
            >
              <CheckCircle className="h-4 w-4" />
              Entendi, obrigado!
            </button>
            {agendamentoRecemCriado && (
              <button
                onClick={abrirCancelamentoDoRecemCriado}
                className="w-full bg-red-500 hover:bg-red-600 text-white py-3 px-4 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 shadow-sm active:scale-95"
              >
                <Trash2 className="h-4 w-4" />
                Cancelar Agendamento
              </button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal Sem Procedimentos */}
      <Sheet
        open={modalAberto === "semProcedimentos"}
        onOpenChange={fecharModal}
      >
        <SheetContent
          className="w-full sm:max-w-7xl mx-auto pt-safe max-h-[85vh] overflow-hidden flex flex-col p-0"
          side="bottom"
        >
          {/* Cabeçalho com botão fechar */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-center relative">
            <button
              onClick={fecharModal}
              className="absolute right-6 rounded-full p-2 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="flex flex-col items-center max-w-2xl mx-auto">
              {/* Ícone principal */}
              <div className="bg-amber-100 rounded-full p-6 mb-6">
                <Clock className="h-12 w-12 text-amber-600" />
              </div>

              {/* Título */}
              <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
                Procedimentos em Breve!
              </h2>

              {/* Mensagem principal */}
              <p className="text-gray-600 mb-8 text-center text-lg leading-6 max-w-lg">
                No momento não temos procedimentos disponíveis para agendamento,
                mas estamos trabalhando para disponibilizar novos serviços em
                breve.
              </p>

              {/* Seção Azul - Cuidando do seu pet */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6 w-full mb-6 border border-blue-200">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-500 rounded-full p-2 mr-3">
                    <Heart className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-blue-800 font-bold text-lg">
                    Cuidando do seu pet
                  </h3>
                </div>

                <p className="text-blue-700 leading-6">
                  Em breve, novos procedimentos veterinários estarão disponíveis
                  para oferecer o melhor cuidado ao seu pet. Aguarde as
                  novidades!
                </p>
              </div>

              {/* Seção Verde - Fique por dentro */}
              <div className="bg-green-50 rounded-2xl p-6 w-full mb-8 border border-green-200">
                <div className="flex items-center mb-4">
                  <div className="bg-green-500 rounded-full p-2 mr-3">
                    <Bell className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-green-800 font-bold text-lg">
                    Fique por dentro
                  </h3>
                </div>

                <p className="text-green-700 leading-6">
                  Assim que os novos procedimentos estiverem disponíveis, você
                  será notificado e poderá agendar através do aplicativo.
                </p>
              </div>
            </div>
          </div>

          {/* Botão de confirmação */}
          <div className="px-6 py-4 border-t border-gray-200">
            <button
              onClick={fecharModal}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 shadow-sm active:scale-95"
            >
              <CheckCircle className="h-5 w-5" />
              Entendi, obrigado!
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal Serviço Indisponível */}
      <Sheet
        open={modalIndisponivelAberto}
        onOpenChange={fecharModalIndisponivel}
      >
        <SheetContent
          className="w-full sm:max-w-7xl mx-auto pt-safe max-h-[85vh] overflow-hidden flex flex-col p-0"
          side="bottom"
        >
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="flex flex-col items-center max-w-2xl mx-auto">
              {/* Ícone principal */}
              <div className="bg-orange-100 rounded-full p-6 mb-6">
                <Clock className="h-12 w-12 text-orange-600" />
              </div>

              {/* Título */}
              <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
                Sem Vagas Disponíveis
              </h2>

              {/* Mensagem principal */}
              <p className="text-gray-600 mb-6 text-center text-lg leading-6 max-w-lg">
                No momento não há vagas disponíveis para o serviço{" "}
                <strong className="text-blue-600">{tipoIndisponivel}</strong>.
                Novas vagas serão abertas em breve
              </p>
            </div>
          </div>

          {/* Botão de confirmação */}
          <div className="px-6 py-4 border-t border-gray-200">
            <button
              onClick={fecharModalIndisponivel}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 shadow-sm active:scale-95"
            >
              <CheckCircle className="h-5 w-5" />
              Entendi, obrigado!
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal de Cancelamento */}
      <Sheet
        open={modalCancelamentoAberto}
        onOpenChange={fecharModalCancelamento}
      >
        <SheetContent
          className="w-full sm:max-w-7xl mx-auto pt-safe max-h-[85vh] overflow-hidden flex flex-col p-0"
          side="bottom"
        >
          <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <SheetHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <X className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <SheetTitle>Cancelar Agendamento</SheetTitle>
                  <SheetDescription>
                    Informe o motivo do cancelamento
                  </SheetDescription>
                </div>
              </div>
            </SheetHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
            {agendamentoSelecionado && (
              <>
                {/* Informações do Agendamento */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Informações do Agendamento
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`rounded-full p-2 ${
                          agendamentoSelecionado.pet.especie === "CACHORRO"
                            ? "bg-blue-100"
                            : "bg-red-100"
                        }`}
                      >
                        {agendamentoSelecionado.pet.especie === "CACHORRO" ? (
                          <Dog
                            className={`h-5 w-5 ${
                              agendamentoSelecionado.pet.especie === "CACHORRO"
                                ? "text-blue-600"
                                : "text-red-600"
                            }`}
                          />
                        ) : (
                          <Cat className="h-5 w-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Pet</p>
                        <p className="font-semibold text-gray-800">
                          {agendamentoSelecionado.pet.nome} - {getEspecieLabel(agendamentoSelecionado.pet.especie)}
                        </p>
                      </div>
                    </div>

                    {agendamentoSelecionado.data && (
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-500">Data</p>
                          <p className="font-semibold text-gray-800">
                            {moment(agendamentoSelecionado.data).format("DD/MM/YYYY")}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-3">
                      <Clock className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Turno</p>
                        <p className="font-semibold text-gray-800 capitalize">
                          {agendamentoSelecionado.turno?.toLowerCase()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Aviso */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <X className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-red-800 mb-1">
                        Atenção: Esta ação é irreversível
                      </p>
                      <p className="text-sm text-red-700">
                        Ao cancelar este agendamento, você não poderá desfazer esta ação. 
                        Certifique-se de que deseja prosseguir.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            <form
              onSubmit={handleSubmitCancelamento(onSubmitCancelamento)}
              className="space-y-6"
            >
              {/* Motivo do Cancelamento */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivo do Cancelamento <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Controller
                    control={controlCancelamento}
                    name="motivo"
                    render={({ field }) => (
                      <textarea
                        {...field}
                        rows={4}
                        placeholder="Descreva o motivo do cancelamento"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                      />
                    )}
                  />
                </div>
                {errorsCancelamento.motivo && (
                  <p className="text-red-500 text-xs mt-1">
                    {errorsCancelamento.motivo.message}
                  </p>
                )}
              </div>

              {/* Botões */}
              <div className="pt-6 space-y-3">
                <button
                  type="submit"
                  disabled={cancelarAgendamentoMutation.isPending}
                  className="w-full bg-red-500 hover:bg-red-600 text-white py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {cancelarAgendamentoMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Confirmar Cancelamento
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={fecharModalCancelamento}
                  disabled={cancelarAgendamentoMutation.isPending}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Manter Agendamento
                </button>
              </div>
            </form>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
